import React, { useEffect, useState } from 'react';
import { feedbackAPI, getFileUrl } from '../../services/api';
import API from '../../services/api';
import { Modal, FormField, Button, Badge, Alert } from '../ui';
import { Send, Paperclip } from 'lucide-react';

const STATUSES = ['Open', 'In Review', 'In Progress', 'Needs Clarification', 'Resolved'];

export default function FeedbackThreadModal({ ticketId, currentUser, isManager, onClose, onUpdated }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = () => {
    setLoading(true);
    feedbackAPI.get(ticketId)
      .then((res) => setTicket(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load ticket'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const uploadFiles = async () => {
    if (!files.length) return [];
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const res = await API.post('/uploads/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.data.map((f) => ({ url: f.url, originalname: f.originalname, mimetype: f.mimetype }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setError('');
    try {
      const attachments = await uploadFiles();
      await feedbackAPI.addMessage(ticketId, { body, attachments });
      setBody('');
      setFiles([]);
      load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending reply');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    setStatusSaving(true);
    try {
      await feedbackAPI.updateStatus(ticketId, status);
      load();
      onUpdated?.();
    } catch (err) {
      alert(err.response?.data?.message || 'Error updating status');
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={ticket ? `${ticket.ticket_number || `FB-${String(ticket.feedback_id).padStart(5, '0')}`} · ${ticket.subject}` : 'Ticket'} width={640}>
      {loading || !ticket ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <Badge value={ticket.category} />
            <Badge value={ticket.priority} />
            <Badge value={ticket.status} />
            {ticket.module_name && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>Module: {ticket.module_name}</span>
            )}
          </div>

          {isManager && (
            <FormField label="Update Status">
              <select value={ticket.status} disabled={statusSaving} onChange={(e) => handleStatusChange(e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </FormField>
          )}

          {!isManager && ticket.status === 'Resolved' && (
            <div style={{ marginBottom: 16, padding: 16, background: 'rgba(108, 99, 255, 0.05)', border: '1px solid var(--accent)', borderRadius: 8 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Manager has marked this as Resolved</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--text-muted)' }}>Please verify if the issue is completely fixed.</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button onClick={() => handleStatusChange('Closed')} disabled={statusSaving} size="sm">
                  <span style={{ color: '#fff' }}>Verify & Close</span>
                </Button>
                <Button variant="danger" onClick={() => handleStatusChange('Open')} disabled={statusSaving} size="sm">
                  Not Done (Reopen)
                </Button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 340, overflowY: 'auto', margin: '16px 0', paddingRight: 4 }}>
            {/* Original ticket message */}
            <ThreadBubble
              name={ticket.client?.name || 'Client'}
              role="Client"
              body={ticket.message}
              attachments={ticket.attachments}
              date={ticket.createdAt}
              isSelf={currentUser?.user_id === ticket.client_id}
            />
            {(ticket.messages || []).map((m) => (
              <ThreadBubble
                key={m.message_id}
                name={m.sender?.name || 'User'}
                role={m.sender?.role}
                body={m.body}
                attachments={m.attachments}
                date={m.createdAt}
                isSelf={currentUser?.user_id === m.sender_id}
              />
            ))}
          </div>

          <Alert type="error" message={error} />

          <form onSubmit={handleSend}>
            <FormField label={isManager ? 'Reply to Client' : 'Add a Follow-up'}>
              <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." required />
            </FormField>
            <FormField label="Attachments (optional)">
              <input type="file" multiple accept="image/*,.pdf" onChange={(e) => setFiles(Array.from(e.target.files))} />
              {files.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {files.map((f, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Paperclip size={12} />{f.name}</span>)}
                </div>
              )}
            </FormField>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
              <Button type="submit" disabled={sending}><Send size={14} /> {sending ? 'Sending...' : 'Send'}</Button>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
}

function ThreadBubble({ name, role, body, attachments, date, isSelf }) {
  return (
    <div style={{
      alignSelf: isSelf ? 'flex-end' : 'flex-start',
      maxWidth: '85%',
      padding: 12,
      borderRadius: 10,
      background: isSelf ? 'rgba(108, 99, 255, 0.08)' : 'var(--bg-card)',
      border: `1px solid ${isSelf ? 'rgba(108, 99, 255, 0.25)' : 'var(--border)'}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{name} {role ? `· ${role}` : ''}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(date).toLocaleString()}</span>
      </div>
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{body}</div>
      {attachments && attachments.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {attachments.map((a, i) => (
            <a key={i} href={getFileUrl(a.url)} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--accent)' }}>
              <Paperclip size={12} /> {a.originalname}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}